// ContractorsPage.tsx — /poa/contractors
//
// The register of Form 2848 representatives who are not BMSS employees.
//
// Everything the POA prefill needs for a representative normally comes from three
// employee-shaped sources: the office address in PE (keyed on office code, shared by
// everyone in that office), CCH StaffService, and iSolved. An outside contractor
// appears in none of them, so their details had to be retyped on every POA — and a
// home address could not be recorded anywhere, because the only address in the chain
// belongs to the office.
//
// A row here is applied over whatever those lookups returned, field by field, and
// before the iSolved fallback runs.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Ban, AlertCircle, X, Users, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import {
  listContractors, saveContractor, deactivateContractor,
  type POAContractor, type POAContractorInput,
} from '../../services/poaContractors';
import { DESIGNATION_CODES, NON_STATE_JURISDICTIONS, US_STATES } from './poaConstants';

const REQUIRES_LICENSE = new Set(
  DESIGNATION_CODES.filter((d) => d.requiresLicense).map((d) => d.value),
);

const EMPTY: POAContractorInput = {
  staffUser: '', fullName: '', address: '', phone: '', fax: '',
  cafNumber: '', ptin: '', designationCode: '', licensingJurisdiction: '',
  licenseNumber: '', notes: '', isActive: true,
};

const inputStyle = (bad?: boolean): React.CSSProperties => ({
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: `1px solid ${bad ? 'var(--bad)' : 'var(--border)'}`,
  background: 'rgba(255,255,255,.04)', color: 'var(--text)',
  fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
});

const selectStyle = (bad?: boolean): React.CSSProperties => ({
  ...inputStyle(bad), cursor: 'pointer', background: '#1e2030', colorScheme: 'dark',
});

function Field({ label, hint, error, children }: {
  label: string; hint?: string; error?: string; children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
        {label}
      </label>
      {children}
      {hint && !error && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{hint}</div>}
      {error && <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 3 }}>{error}</div>}
    </div>
  );
}

// ── Editor ───────────────────────────────────────────────────────────────────
function ContractorModal({ initial, onSaved, onClose }: {
  initial: POAContractorInput;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [form, setForm]   = useState<POAContractorInput>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const set = <K extends keyof POAContractorInput>(k: K, v: POAContractorInput[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const needsLicense = REQUIRES_LICENSE.has(form.designationCode);

  // Mirrors the POA form's own rules, so a row saved here can never prefill a POA
  // into a state the POA validator immediately rejects.
  const errors: Record<string, string> = {};
  if (!form.fullName.trim()) errors.fullName = 'Required — the legal name as it should appear on the form.';
  if (form.staffUser.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.staffUser.trim()))
    errors.staffUser = 'Must be a valid email address.';
  if (form.ptin.trim() && !/^P\d{8}$/i.test(form.ptin.trim()))
    errors.ptin = 'PTIN must be in the format P00000000.';
  if (needsLicense && !form.licensingJurisdiction.trim())
    errors.licensingJurisdiction = 'Required for this designation.';
  if (needsLicense && !form.licenseNumber.trim())
    errors.licenseNumber = 'Required for this designation.';

  const canSave = Object.keys(errors).length === 0;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await saveContractor(form);
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--surface, #1e2030)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 24, width: 720, maxWidth: '95vw',
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,.6)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {form.id ? 'Edit contractor' : 'Add contractor'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3, lineHeight: 1.5, maxWidth: 520 }}>
              These values are applied over the office and CCH lookups when this person
              is chosen as a representative, and before the iSolved fallback runs.
            </div>
          </div>
          <button className="btn" style={{ padding: '4px 8px' }} onClick={onClose}><X size={14} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Full name *" error={errors.fullName}
            hint="As it should read on Form 2848, Part II.">
            <input style={inputStyle(!!errors.fullName)} value={form.fullName}
              placeholder="e.g. Kelly Myers"
              onChange={(e) => set('fullName', e.target.value)} />
          </Field>

          <Field label="Sign-in address" error={errors.staffUser}
            hint="Their bmss.com address if they have one — it is the reliable match key.">
            <input style={inputStyle(!!errors.staffUser)} value={form.staffUser}
              placeholder="e.g. kmyers@bmss.com"
              onChange={(e) => set('staffUser', e.target.value)} />
          </Field>
        </div>

        <Field label="Address"
          hint="Goes on Part I, Line 2. Leave blank to keep using their office address.">
          <input style={inputStyle()} value={form.address}
            placeholder="e.g. 2902 Pasture View Ln SE, Owens Cross Roads, AL 35763"
            onChange={(e) => set('address', e.target.value)} />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Phone">
            <input style={inputStyle()} value={form.phone} placeholder="e.g. 256-555-0100"
              onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label="Fax">
            <input style={inputStyle()} value={form.fax} placeholder="e.g. 256-555-0199"
              onChange={(e) => set('fax', e.target.value)} />
          </Field>
        </div>

        <div style={{
          borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4, marginBottom: 4,
          fontSize: 12, color: 'var(--muted)', fontWeight: 600,
          letterSpacing: .5, textTransform: 'uppercase',
        }}>
          Part II — Declaration of Representative
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Field label="CAF number">
            <input style={inputStyle()} value={form.cafNumber} placeholder="e.g. 1234-56789R"
              onChange={(e) => set('cafNumber', e.target.value)} />
          </Field>
          <Field label="PTIN" error={errors.ptin}>
            <input style={inputStyle(!!errors.ptin)} value={form.ptin} placeholder="e.g. P12345678"
              onChange={(e) => set('ptin', e.target.value)} />
          </Field>
          <Field label="Designation">
            <select style={selectStyle()} value={form.designationCode}
              onChange={(e) => set('designationCode', e.target.value)}>
              <option value="">— none —</option>
              {DESIGNATION_CODES.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label={`Licensing jurisdiction${needsLicense ? ' *' : ''}`}
            error={errors.licensingJurisdiction}>
            <select style={selectStyle(!!errors.licensingJurisdiction)} value={form.licensingJurisdiction}
              onChange={(e) => set('licensingJurisdiction', e.target.value)}>
              <option value="">— none —</option>
              <optgroup label="Federal / other licensing authority">
                {NON_STATE_JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
              </optgroup>
              <optgroup label="State or territory">
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </optgroup>
            </select>
          </Field>
          <Field label={`Bar / license / enrollment number${needsLicense ? ' *' : ''}`}
            error={errors.licenseNumber}>
            <input style={inputStyle(!!errors.licenseNumber)} value={form.licenseNumber}
              onChange={(e) => set('licenseNumber', e.target.value)} />
          </Field>
        </div>

        <Field label="Notes" hint="Context for whoever maintains this — firm, engagement, who to ask.">
          <input style={inputStyle()} value={form.notes}
            onChange={(e) => set('notes', e.target.value)} />
        </Field>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 18, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isActive}
            onChange={(e) => set('isActive', e.target.checked)} />
          Active — appears in the representative picker
        </label>

        {error && (
          <div style={{
            fontSize: 13, color: 'var(--bad)', marginBottom: 14,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={15} /> {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose} style={{ fontSize: 14 }}>Cancel</button>
          <button
            className="btn"
            onClick={handleSave}
            disabled={!canSave || saving}
            style={{
              fontSize: 14, fontWeight: 600,
              background: 'rgba(122,162,255,.15)', borderColor: 'rgba(122,162,255,.5)',
              color: 'var(--accent)', opacity: (!canSave || saving) ? .45 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save contractor'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ContractorsPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [rows, setRows]       = useState<POAContractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [editing, setEditing] = useState<POAContractorInput | null>(null);
  const [busy, setBusy]       = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listContractors());
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDeactivate(row: POAContractor) {
    if (!window.confirm(
      `Deactivate ${row.fullName}? They stop appearing in the representative picker. ` +
      `Existing POAs are unaffected — the details already on them stay as filed.`
    )) return;
    setBusy(row.id);
    try {
      await deactivateContractor(row.id);
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  }

  const desLabel = (code: string) =>
    DESIGNATION_CODES.find((d) => d.value === code)?.label.split('—')[1]?.trim() ?? code ?? '—';

  const activeCount = rows.filter((r) => r.isActive).length;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 16px' }}>
      <button
        onClick={() => navigate('/poa')}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center',
          gap: 4, marginBottom: 12,
        }}
      >
        <ArrowLeft size={13} /> All POAs
      </button>

      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 8, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 9 }}>
            <Users size={19} /> Contractor Representatives
          </h2>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            {rows.length} record{rows.length === 1 ? '' : 's'}
            {rows.length > 0 && <> &ensp;·&ensp; {activeCount} active</>}
          </div>
        </div>
        {isAdmin && (
          <button
            className="btn"
            style={{
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(122,162,255,.12)', borderColor: 'rgba(122,162,255,.4)',
              color: 'var(--accent)',
            }}
            onClick={() => setEditing({ ...EMPTY })}
          >
            <Plus size={14} /> Add contractor
          </button>
        )}
      </div>

      <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.6, maxWidth: 760, marginTop: 4 }}>
        Representatives who sign our Forms 2848 but are not BMSS employees. The usual
        prefill sources only describe employees — and the address in that chain is the
        shared <em>office</em> address, not a personal one — so a contractor's details
        live here instead. They are applied over the office and CCH values when the
        person is picked, and before the iSolved fallback runs.
        {!isAdmin && (
          <>
            {' '}Editing is admin-only: a wrong CAF or PTIN here lands on every Form 2848
            that representative signs.
          </>
        )}
      </p>

      {error && (
        <div style={{
          color: 'var(--bad)', fontSize: 13, marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {loading && <div style={{ color: 'var(--muted)', fontSize: 14, padding: '20px 0' }}>Loading…</div>}

      {!loading && !error && rows.length === 0 && (
        <div style={{
          color: 'var(--muted)', fontSize: 14, padding: '40px 20px', textAlign: 'center',
          border: '1px dashed var(--border)', borderRadius: 10, marginTop: 12,
        }}>
          No contractors recorded yet.
          {isAdmin && <> Add one and their details will prefill on every POA they sign.</>}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', marginTop: 12 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Name', 'Address', 'Designation', 'CAF', 'PTIN', 'Status', ''].map((h) => (
                    <th key={h} style={{
                      padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                      color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5,
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{
                    borderBottom: '1px solid var(--border)',
                    opacity: r.isActive ? 1 : .5,
                  }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600 }}>{r.fullName}</div>
                      {r.staffUser && (
                        <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>{r.staffUser}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--muted)', maxWidth: 320 }}>
                      {r.address || <span style={{ fontStyle: 'italic' }}>uses office address</span>}
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      {r.designationCode ? desLabel(r.designationCode) : '—'}
                      {r.licensingJurisdiction && (
                        <div style={{ fontSize: 11, marginTop: 2 }}>{r.licensingJurisdiction}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{r.cafNumber || '—'}</td>
                    <td style={{ padding: '12px 14px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{r.ptin || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 9px', borderRadius: 20,
                        fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                        background: r.isActive ? 'rgba(158,206,106,.12)' : 'rgba(255,255,255,.06)',
                        color: r.isActive ? '#9ece6a' : 'var(--muted)',
                        border: `1px solid ${r.isActive ? 'rgba(158,206,106,.3)' : 'var(--border)'}`,
                      }}>
                        {r.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            className="btn"
                            style={{ fontSize: 12, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                            onClick={() => setEditing({ ...r })}
                          >
                            <Pencil size={11} /> Edit
                          </button>
                          {r.isActive && (
                            <button
                              className="btn"
                              disabled={busy === r.id}
                              style={{
                                fontSize: 12, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4,
                                color: 'var(--bad)', borderColor: 'rgba(247,118,142,.35)',
                                background: 'rgba(247,118,142,.07)',
                                opacity: busy === r.id ? .5 : 1,
                              }}
                              onClick={() => handleDeactivate(r)}
                            >
                              <Ban size={11} /> {busy === r.id ? '…' : 'Deactivate'}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <ContractorModal
          initial={editing}
          onSaved={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
